import { useEffect, useState } from 'react';

import {
  useBankReconciliationDraft,
  type CashAccountBalance,
  type CashLedgerRow,
  type CreateBankReconciliationInput,
} from '@/api/cash';
import { DataView, type DataViewColumn } from '@/components/DataView';
import { enterKeyScope, useAutoFocusFirstField } from '@/components/enterKeyNavigation';
import { Button, Card, EmptyState, Notice, PageHeading } from '@/components/PageHeading';
import { Field, controlClass, selectClass } from '@/components/Field';
import { StatusBadge } from '@/components/StatusBadge';
import { formatAmount } from '@core/format/money';
import { formatDate, toIsoDate } from '@core/format/dates';
import { sourceLabels } from '@core/cash/position';
import {
  defaultClearedOn,
  leftOver,
  reconcileLabel,
  reconcileProblem,
  signedOf,
  tickedTotal,
} from '@core/cash/reconcile';
import {
  freshDraft,
  loadReconcileDraft,
  saveReconcileDraft,
  type ReconcileDraft,
} from './reconcileDraft';

/**
 * Matching a bank account against its statement (Cash & bank step 7).
 *
 * <p>
 * <strong>A page, not a drawer</strong> (2026-09-08), reached by internal state from the cash book.
 * Tick each line the statement shows; what is left over is shown as it is typed, and Save stays shut
 * until it is nothing — a difference is a voucher not yet recorded, never an adjustment.
 * </p>
 */
export function ReconcileBankPage({
  branchId,
  accounts,
  initialAccountId,
  busy = false,
  onCancel,
  onSave,
}: {
  branchId: number | undefined;
  /** The branch's bank accounts — see `reconcilableAccounts`. */
  accounts: CashAccountBalance[];
  initialAccountId: number | null;
  busy?: boolean;
  onCancel: () => void;
  onSave: (accountId: number, input: CreateBankReconciliationInput) => void;
}) {
  const firstField = useAutoFocusFirstField();
  const [today] = useState(() => toIsoDate(new Date()) ?? '');

  const [accountId, setAccountId] = useState<number | null>(
    initialAccountId ?? accounts[0]?.id ?? null,
  );
  const [draft, setDraft] = useState<ReconcileDraft>(() =>
    accountId === null ? freshDraft(today) : loadReconcileDraft(accountId, today),
  );

  useEffect(() => {
    if (accountId !== null) {
      saveReconcileDraft(accountId, draft);
    }
  }, [accountId, draft]);

  const server = useBankReconciliationDraft(branchId, accountId ?? undefined, draft.statementDate);
  const rows = server.data?.rows ?? [];

  // Ticks on rows another statement has since cleared are dropped rather than sent: the server
  // would refuse them, and the clerk could not see why.
  const ticks = Object.fromEntries(
    Object.entries(draft.ticks).filter(([id]) => rows.some((row) => row.id === Number(id))),
  ) as Record<number, string>;

  const ticked = tickedTotal(rows, ticks);
  const clearedSoFar = server.data?.clearedSoFar ?? 0;
  const statement = Number(draft.statementBalance);
  const difference =
    draft.statementBalance.trim() === '' || !Number.isFinite(statement)
      ? 0
      : leftOver(statement, clearedSoFar, ticked);

  const problem =
    accountId === null
      ? 'This branch has no bank account to reconcile.'
      : reconcileProblem({
          statementDate: draft.statementDate,
          today,
          lastStatementDate: server.data?.lastStatementDate ?? null,
          statementBalance: draft.statementBalance,
          ticks,
          leftOver: difference,
        });

  const change = (patch: Partial<ReconcileDraft>) => setDraft((was) => ({ ...was, ...patch }));

  const pickRows = (keys: number[]) =>
    change({
      ticks: Object.fromEntries(
        keys.map((key) => {
          const row = rows.find((candidate) => candidate.id === key);

          return [key, ticks[key] ?? (row ? defaultClearedOn(row, draft.statementDate) : '')];
        }),
      ),
    });

  const upToStatement = rows
    .filter((row) => row.occurredOn.slice(0, 10) <= draft.statementDate)
    .map((row) => row.id);

  const columns: DataViewColumn<CashLedgerRow>[] = [
    {
      title: 'Day',
      dataIndex: 'occurredOn',
      primary: true,
      render: (value: string) => (
        <span className="flex flex-col">
          <span>{formatDate(value)}</span>
          {draft.statementDate !== '' && value.slice(0, 10) > draft.statementDate && (
            <span className="text-default text-[11px]">after the statement</span>
          )}
        </span>
      ),
    },
    {
      title: 'What',
      dataIndex: 'sourceKind',
      render: (_: unknown, row: CashLedgerRow) => (
        <span className="flex flex-col">
          <span>{sourceLabels[row.sourceKind]}</span>
          <span className="text-default text-[11px]">
            {[row.sourceDocumentNumber, row.reference, row.notes].filter(Boolean).join(' · ') ||
              '—'}
          </span>
        </span>
      ),
    },
    {
      title: 'Cleared on',
      dataIndex: 'id',
      render: (id: number) =>
        id in ticks ? (
          <input
            type="date"
            aria-label="Cleared on"
            max={draft.statementDate || today}
            value={ticks[id]}
            disabled={busy}
            onChange={(event) => change({ ticks: { ...ticks, [id]: event.target.value } })}
            className={controlClass}
          />
        ) : (
          <span className="text-default text-[12px]">Not on this statement</span>
        ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      align: 'right',
      trailing: true,
      render: (_: unknown, row: CashLedgerRow) => {
        const signed = signedOf(row);

        return (
          <span className={signed < 0 ? 'text-danger font-semibold' : 'text-success font-semibold'}>
            {signed < 0 ? '− ' : '+ '}
            {formatAmount(Math.abs(signed))}
          </span>
        );
      },
    },
  ];

  return (
    <div className="flex flex-col gap-3 lg:gap-4" {...enterKeyScope}>
      <PageHeading
        title="Reconcile a bank"
        subtitle="Tick each line the statement shows — it has to come out exactly"
        actions={
          <>
            <Button onClick={onCancel} disabled={busy} shortcut="back">
              Cancel
            </Button>
            <Button
              variant="primary"
              icon="icon icon-check"
              data-enter-key-commit
              shortcut="save"
              disabled={problem !== null || busy || accountId === null}
              onClick={() =>
                accountId !== null &&
                onSave(accountId, {
                  statementDate: draft.statementDate,
                  statementBalance: statement,
                  lines: Object.entries(ticks).map(([id, clearedOn]) => ({
                    cashLedgerEntryId: Number(id),
                    clearedOn,
                  })),
                  notes: draft.notes.trim() || null,
                })
              }
            >
              {reconcileLabel(draft.statementDate, formatDate)}
            </Button>
          </>
        }
      />

      {accounts.length === 0 && (
        <Notice tone="warning" title="No bank account">
          A bank account appears once money has moved by UPI, card or transfer, or once one is
          opened on the cash book.
        </Notice>
      )}

      {server.data && !server.data.hasOpeningBalance && server.data.lastStatementDate === null && (
        <Notice tone="warning" title="Record the opening balance first">
          {server.data.accountName} has no opening balance, so the money it held before the cash
          book started is missing from the lines below and the first statement cannot come out.
          Record it on the cash book, then tick it here.
        </Notice>
      )}

      <div className="grid gap-3 lg:grid-cols-2 lg:gap-4">
        <Card>
          <p className="mb-2.5 text-sm font-semibold text-gray-900">The statement</p>

          <div className="flex flex-col gap-3">
            <Field label="Bank account" required>
              <select
                ref={firstField}
                disabled={busy || accounts.length === 0}
                value={accountId ?? ''}
                onChange={(event) => {
                  const next = Number(event.target.value);

                  setAccountId(next);
                  setDraft(loadReconcileDraft(next, today));
                }}
                className={selectClass}
              >
                {accounts.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                    {option.bankReference ? ` · ${option.bankReference}` : ''}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Closes on" required hint="The last day the statement covers.">
              <input
                type="date"
                max={today}
                disabled={busy}
                value={draft.statementDate}
                onChange={(event) => change({ statementDate: event.target.value })}
                className={controlClass}
              />
            </Field>

            <Field
              label="Closing balance"
              required
              hint="As printed on the statement. Overdrawn is a minus figure."
            >
              <input
                type="number"
                step="0.01"
                disabled={busy}
                value={draft.statementBalance}
                onChange={(event) => change({ statementBalance: event.target.value })}
                onWheel={(event) => event.currentTarget.blur()}
                className={controlClass}
              />
            </Field>

            <Field label="Notes">
              <textarea
                rows={2}
                disabled={busy}
                value={draft.notes}
                onChange={(event) => change({ notes: event.target.value })}
                className={controlClass}
              />
            </Field>
          </div>
        </Card>

        <Card>
          <p className="mb-2.5 text-sm font-semibold text-gray-900">Against the book</p>

          <dl className="flex flex-col gap-2">
            <Line
              label={
                server.data?.lastStatementDate
                  ? `Cleared to ${formatDate(server.data.lastStatementDate)}`
                  : 'Cleared before (first statement)'
              }
              value={formatAmount(clearedSoFar)}
            />
            <Line
              label={`Ticked now · ${Object.keys(ticks).length} of ${rows.length} lines`}
              value={`${ticked < 0 ? '− ' : ''}${formatAmount(Math.abs(ticked))}`}
            />
            <Line
              label="The statement says"
              value={
                draft.statementBalance.trim() === '' || !Number.isFinite(statement)
                  ? '—'
                  : formatAmount(statement)
              }
            />

            <div className="border-border-color flex items-baseline justify-between gap-2 border-t pt-2">
              <dt className="flex items-center gap-1.5 text-[length:var(--scale-list-name)] font-semibold text-gray-900">
                Left over
                {draft.statementBalance.trim() !== '' && (
                  <StatusBadge tone={difference === 0 ? 'positive' : 'warning'}>
                    {difference === 0 ? 'Balances' : 'Not yet'}
                  </StatusBadge>
                )}
              </dt>
              <dd className="text-lg font-bold text-gray-900">
                {formatAmount(Math.abs(difference))}
              </dd>
            </div>
          </dl>

          {server.data && (
            <p className="text-default mt-3 text-[length:var(--scale-list-meta)]">
              The cash book says {server.data.accountName} held{' '}
              {formatAmount(server.data.bookBalance)} at the close of{' '}
              {formatDate(server.data.statementDate)}. That figure is kept with the match.
            </p>
          )}

          {problem && accounts.length > 0 && (
            <p className="text-warning mt-3 text-[length:var(--scale-list-meta)]">{problem}</p>
          )}
        </Card>
      </div>

      <Card>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm font-semibold text-gray-900">Lines not yet cleared</p>
          <span className="flex gap-1.5">
            <Button
              disabled={busy || upToStatement.length === 0}
              onClick={() => pickRows(upToStatement)}
            >
              Tick all to {draft.statementDate ? formatDate(draft.statementDate) : 'the statement'}
            </Button>
            <Button disabled={busy || Object.keys(ticks).length === 0} onClick={() => pickRows([])}>
              Clear ticks
            </Button>
          </span>
        </div>

        <DataView
          columns={columns}
          dataSource={rows}
          loading={server.isLoading}
          selectedKeys={Object.keys(ticks).map(Number)}
          onSelectionChange={pickRows}
          empty={
            <EmptyState
              icon="icon icon-check"
              title="Every line is cleared"
              hint="Everything this bank has moved is on a statement already matched."
            />
          }
        />
      </Card>
    </div>
  );
}

function Line({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="text-default text-[length:var(--scale-list-meta)]">{label}</dt>
      <dd className="text-[length:var(--scale-list-name)] font-semibold text-gray-900">{value}</dd>
    </div>
  );
}
